import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { css, cx } from '@emotion/css'
import { Row, Box } from '../../../legacy-design-system/form'
import {
  ServerEnvironment,
  serverEnvI18n,
  allServerEnvironments
} from '../../server-environment'
import { BaseAssessRule } from '../../assess-rules'
import { ApplicationImportance } from '../../../graphql/schema/types/application-importance'
import {
  AutoVerificationType,
  VulnerabilityPolicyAction,
  BaseApplication
} from '..'
import { FormattedMessage } from 'react-intl'
import { IconHelp } from '@contrast-security-inc/design-system-foundations'
import {
  allRuleSeverities,
  normalizeAssessRulesToTypedVulnerabilities,
  normalizeRuleSeveritiesToTypedVulnerabilities,
  TypedVulnerabilities
} from '../../shared'
import {
  MultiSelect,
  Radio,
  Checkbox,
  TextInput
} from '@contrast-security-inc/design-system-inputs'
import { Badge } from '@contrast-security-inc/design-system-badges'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import debounce from 'lodash.debounce'
import { useApplicationSearchFilter } from './api/use-application-search-filter'
import { string, number, object, boolean } from 'yup'
import { formSectionTitleCss } from '../../../legacy-design-system/text'

type ApplicationType = 'importance' | 'application'

const allApplicationImportance = [
  {
    value: 'CRITICAL',
    name: 'title_appImportanceLevelCritical'
  },
  {
    value: 'HIGH',
    name: 'title_appImportanceLevelHigh'
  },
  {
    value: 'MEDIUM',
    name: 'title_appImportanceLevelMedium'
  },
  {
    value: 'LOW',
    name: 'title_appImportanceLevelLow'
  },
  {
    value: 'UNIMPORTANT',
    name: 'title_appImportanceLevelUnimportant'
  }
]

export const defaultDaysOnDisabled = 0

export function setAndNormalizeRules(applications: TypedVulnerabilities[]) {
  const rules: TypedVulnerabilities[] = []
  const severities: TypedVulnerabilities[] = []
  applications.forEach((rule) => {
    if (rule?.type === 'rule') {
      rules.push(rule)
    } else if (rule?.type === 'severity') {
      severities.push(rule)
    }
  })

  return { rules, severities }
}

export function normalizeImportanceTyped(
  importanceItems,
  intl: any
): TypedApplications[] {
  return importanceItems.map(({ name, value }) => ({
    name: intl.formatMessage({ id: name }),
    id: value,
    type: 'importance'
  }))
}

export function normalizeApplicationsTyped(
  rules: BaseApplication[]
): TypedApplications[] {
  return rules.map(({ name, id }) => ({ name, id, type: 'application' }))
}

export function setAndNormalizeApplications(applications: TypedApplications[]) {
  const apps: TypedApplications[] = []
  const importance: TypedApplications[] = []
  applications.forEach((app) => {
    if (app?.type === 'application') {
      apps.push(app)
    } else if (app?.type === 'importance') {
      importance.push(app)
    }
  })

  return { apps, importance }
}

export interface TypedApplications {
  readonly id: string | ApplicationImportance
  readonly name: string
  readonly type: ApplicationType
}

interface Props {
  readonly intl: any
  readonly baseAssessRules: BaseAssessRule[]
  readonly name: string
  readonly setName: React.Dispatch<React.SetStateAction<string>>
  readonly days: number
  readonly setDays: React.Dispatch<React.SetStateAction<number>>

  readonly vulnAutoVerificationEnabled: boolean

  readonly rules: TypedVulnerabilities[]
  readonly setRules: React.Dispatch<
    React.SetStateAction<TypedVulnerabilities[]>
  >
  readonly ruleSeverities: TypedVulnerabilities[]
  readonly setRuleSeverities: React.Dispatch<
    React.SetStateAction<TypedVulnerabilities[]>
  >

  readonly applications: TypedApplications[]
  readonly setApplications: React.Dispatch<
    React.SetStateAction<TypedApplications[]>
  >
  readonly applicationImportance: TypedApplications[]
  readonly setApplicationImportance: React.Dispatch<
    React.SetStateAction<TypedApplications[]>
  >

  readonly serverEnvironments: ServerEnvironment[]
  readonly setServerEnvironments: React.Dispatch<
    React.SetStateAction<ServerEnvironment[]>
  >

  readonly action: VulnerabilityPolicyAction
  readonly isDaysEnabled: boolean
  readonly setIsDaysEnabled: React.Dispatch<React.SetStateAction<boolean>>

  autoVerificationType: AutoVerificationType
  setAutoVerificationType: React.Dispatch<
    React.SetStateAction<AutoVerificationType>
  >
  organizationID: string
  setIsValidForm: React.Dispatch<React.SetStateAction<boolean>>
}

interface FormSchema {
  autoVerificationName: string
  autoVerificationNumberDays: number
  verifyAutoRemediated: boolean
  autoVerifyByRouteOrSession: boolean
}

export const VulnerabilityPolicyForm: React.FC<Props> = ({
  intl,
  baseAssessRules,
  name,
  setName,
  days,
  setDays,
  rules,
  setRules,
  ruleSeverities,
  setRuleSeverities,
  applications,
  setApplications,
  applicationImportance,
  setApplicationImportance,
  serverEnvironments,
  setServerEnvironments,
  action,
  isDaysEnabled,
  setIsDaysEnabled,
  vulnAutoVerificationEnabled,
  autoVerificationType,
  setAutoVerificationType,
  organizationID,
  setIsValidForm
}) => {
  const normalizeApplications = applications.concat(applicationImportance)
  const normalizeRules = rules.concat(ruleSeverities)
  const isPolicyViolationAction = action === 'NOTIFY'
  const [
    enableAutoVerificationTypeChoices,
    setEnableAutoVerificationTypeChoices
  ] = useState(
    autoVerificationType && autoVerificationType !== AutoVerificationType.NONE
  )

  const [isAppSearchLoading, setIsAppSearchLoading] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState<string>('')

  const searchApplicationsFilter = useApplicationSearchFilter({
    organizationUuid: organizationID,
    searchTerm
  })

  const appSearchIsLoading = React.useMemo(
    () =>
      isAppSearchLoading ||
      searchApplicationsFilter.isLoading ||
      !searchApplicationsFilter.data,
    [
      isAppSearchLoading,
      searchApplicationsFilter?.isLoading,
      searchApplicationsFilter?.data
    ]
  )

  const filteredApplications: BaseApplication[] =
    appSearchIsLoading || !searchApplicationsFilter.data
      ? []
      : searchApplicationsFilter.data.applications.map((item) => ({
          id: item.appId,
          name: item.appName
        }))

  const setAppSearchTermCallback = React.useCallback(
    (data) => {
      setIsAppSearchLoading(false)
      setSearchTerm(data)
    },
    [setSearchTerm, setIsAppSearchLoading]
  )

  const debouncedSetAppSearchTerm = React.useMemo(
    () => debounce(setAppSearchTermCallback, 500),
    [setAppSearchTermCallback]
  )

  const defaultOneDayOnDisabled = defaultDaysOnDisabled + 1

  const formSchema = object({
    autoVerificationName: string().required(
      intl.formatMessage({
        id: 'validation_updateNameSuggested_requiredName'
      })
    ),
    autoVerificationNumberDays: number()
      .required(
        intl.formatMessage({
          id: 'validation_remediationPolicyCustomization_requiredRemediationDays'
        })
      )
      .min(isDaysEnabled ? 1 : defaultOneDayOnDisabled),
    verifyAutoRemediated: boolean(),
    autoVerifyByRouteOrSession: boolean()
  }).test('at-least-one-checkbox', '', (values) => {
    return (
      values.verifyAutoRemediated === true ||
      values.autoVerifyByRouteOrSession === true
    )
  })

  const {
    control,
    watch,
    formState: { errors, isValid }
  } = useForm<FormSchema>({
    resolver: yupResolver(formSchema),
    mode: 'all',
    defaultValues: {
      autoVerificationName: name,
      autoVerificationNumberDays:
        isDaysEnabled && !!days ? days : defaultOneDayOnDisabled,
      verifyAutoRemediated: isDaysEnabled,
      autoVerifyByRouteOrSession: enableAutoVerificationTypeChoices
    }
  })

  const autoVerificationNumberDaysControl = 'autoVerificationNumberDays'

  React.useEffect(() => {
    if (setIsValidForm) {
      setIsValidForm(isValid)
    }
  }, [isValid, setIsValidForm])

  React.useEffect(() => {
    const subscription = watch((value) => {
      if (setDays) {
        setDays(value?.autoVerificationNumberDays ?? defaultOneDayOnDisabled)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setDays])

  return (
    <div
      className={css`
        margin-left: 10px;
      `}
    >
      <form>
        {action === 'AUTO_REMEDIATE' && (
          <Row>
            <Box basis={9}>
              <FormattedMessage id="detail_vulnManagement_policy_autoVerifyHeaderMessage" />
            </Box>
          </Row>
        )}
        <Row>
          <Box basis={9}>
            <Controller
              name="autoVerificationName"
              control={control}
              render={({ field: { value, onChange, ref, onBlur } }) => (
                <div>
                  <TextInput
                    id="auto-verification-name"
                    data-e2e="auto-verification-name"
                    data-testid="auto-verification-name"
                    label={intl.formatMessage({ id: 'title_name' })}
                    placeholder={intl.formatMessage({
                      id: 'placeholder_sourceNames_name'
                    })}
                    isRequired={true}
                    value={value}
                    onChange={(event) => {
                      onChange(event)
                      setName(event.target.value)
                    }}
                    onBlur={onBlur}
                    ref={ref}
                    error={errors.autoVerificationName?.message}
                  />
                </div>
              )}
            />
          </Box>
        </Row>
        <Row>
          <Box basis={9}>
            <MultiSelect<TypedVulnerabilities>
              id="auto-verification-vulnerabilities"
              data-testid="auto-verification-vulnerabilities"
              data-e2e="auto-verification-vulnerabilities-input"
              label={intl.formatMessage({ id: 'title_vulnerabilityRules' })}
              placeholderTag={`${intl.formatMessage({
                id: 'title_allRules'
              })} (${baseAssessRules.length})`}
              itemIsDisabled={(item) =>
                ['severityLevelLabel', 'assessRulesLabel'].includes(item.id)
              }
              items={[
                {
                  id: 'severityLevelLabel',
                  name: intl.formatMessage({ id: 'title_severityLevel' }),
                  type: 'severity'
                },
                ...normalizeRuleSeveritiesToTypedVulnerabilities(
                  allRuleSeverities,
                  intl
                ),
                {
                  id: 'assessRulesLabel',
                  name: intl.formatMessage({ id: 'title_assessRules' }),
                  type: 'rule'
                },
                ...normalizeAssessRulesToTypedVulnerabilities(baseAssessRules)
              ]}
              itemToString={(item) => (item ? item.name : '')}
              itemToID={(item) => `${item.type}-${item.id}`}
              onChange={(_, value) => {
                const items = setAndNormalizeRules(value ? value : [])
                setRules(items.rules)
                setRuleSeverities(items.severities)
              }}
              initialSelectedItems={normalizeRules}
            />
          </Box>
        </Row>
        <Row>
          <Box basis={9}>
            <MultiSelect<TypedApplications>
              id="auto-verification-applications"
              data-testid="auto-verification-applications"
              data-e2e="auto-verification-applications"
              label={intl.formatMessage({ id: 'title_applications' })}
              initialSelectedItems={normalizeApplications}
              isAsync={true}
              placeholderTag={`${intl.formatMessage({
                id: 'title_allApplications'
              })}`}
              itemToID={(item) => `${item.type}-${item.id}`}
              itemToString={(item) => item?.name || ''}
              items={[
                ...normalizeImportanceTyped(allApplicationImportance, intl),
                ...normalizeApplicationsTyped(filteredApplications)
              ]}
              onChange={(_, value: TypedApplications[]) => {
                const items = setAndNormalizeApplications(value)
                setApplications(items.apps)
                setApplicationImportance(items.importance)
                setSearchTerm('')
              }}
              onInputChange={(inputValue: string) => {
                if (inputValue) {
                  setIsAppSearchLoading(true)
                  debouncedSetAppSearchTerm(inputValue)
                }
              }}
              style={{ zIndex: 100 }}
            />
          </Box>
        </Row>
        <Row>
          <Box basis={9}>
            <MultiSelect<ServerEnvironment>
              label={intl.formatMessage({
                id: 'title_environment_server'
              })}
              placeholderTag={`${intl.formatMessage({
                id: 'title_allEnvironments'
              })} (3)`}
              initialSelectedItems={serverEnvironments}
              onChange={(_, value) => {
                if (Array.isArray(value)) {
                  setServerEnvironments(value)
                }
              }}
              items={[...allServerEnvironments]}
              itemToString={(environment) =>
                environment
                  ? intl.formatMessage({
                      id: serverEnvI18n[environment]
                    })
                  : ''
              }
            />
          </Box>
        </Row>
        <Row>
          <Box basis={9}>
            <div
              className={cx(
                formSectionTitleCss,
                css`
                  padding-top: 20px;
                  padding-bottom: 10px;
                  display: flex;
                  flex-direction: row;
                `
              )}
            >
              <div>
                <FormattedMessage id="title_vulnerabilityPolicy_trigger" />
              </div>
            </div>
            <FormattedMessage id="detail_vulnManagement_policy_triggerDescriptionMessage" />
          </Box>
        </Row>
        {!isDaysEnabled &&
          autoVerificationType === AutoVerificationType.NONE && (
            <Row>
              <Box basis={9}>
                <div
                  className={css`
                    color: red;
                  `}
                >
                  <FormattedMessage id="detail_vulnManagement_policy_triggerAlertMessage" />
                </div>
              </Box>
            </Row>
          )}
        <Row>
          <Box basis={9}>
            <div
              className={css`
                display: flex;
                flex-direction: row;
                align-items: center;
              `}
            >
              <div
                className={css`
                  padding-top: 8px;
                  margin-right: 10px;
                `}
              >
                <Controller
                  name="verifyAutoRemediated"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Checkbox
                      label={
                        action === 'NOTIFY'
                          ? `${intl.formatMessage({
                              id: 'detail_vulnManagement_policy_triggerFlagAfterDaysMessage'
                            })} `
                          : `${intl.formatMessage({
                              id: 'detail_vulnManagement_policy_triggerAutoVerifyAfterDaysMessage'
                            })} `
                      }
                      isChecked={value}
                      value={String(value)}
                      onChange={(event, value) => {
                        onChange(event)
                        setDays(
                          !!value
                            ? defaultOneDayOnDisabled
                            : defaultDaysOnDisabled
                        )
                        setIsDaysEnabled(value ?? false)
                      }}
                      testId={'auto-verification-trigger-checkbox'}
                    />
                  )}
                />
              </div>
              <div
                className={css`
                  input {
                    width: 55px;
                  }
                  div {
                    flex-direction: row;
                    align-items: center;
                    padding-left: 3px;
                  }
                `}
              >
                <Controller
                  name={autoVerificationNumberDaysControl}
                  control={control}
                  render={({
                    field: { value, onChange, ref, onBlur },
                    fieldState: { error }
                  }) => (
                    <div>
                      <TextInput
                        id="auto-verification-number-days"
                        data-testid="auto-verification-number-days"
                        data-e2e="auto-verification-number-days"
                        type="number"
                        label={intl.formatMessage({
                          id: 'title_remediationPolicyCustomization_part2RemediationDaysPlural'
                        })}
                        isRequired={true}
                        isDisabled={!isDaysEnabled}
                        value={
                          !isDaysEnabled
                            ? String(defaultOneDayOnDisabled)
                            : value.toString()
                        }
                        onChange={(event) => {
                          onChange(event)
                        }}
                        ref={ref}
                        onBlur={onBlur}
                        error={error?.message?.replace(
                          autoVerificationNumberDaysControl,
                          ''
                        )}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </Box>
        </Row>
        {!isPolicyViolationAction && vulnAutoVerificationEnabled && (
          <Row>
            <Box basis={9}>
              <div
                className={css`
                  display: flex;
                  flex-direction: row;
                `}
              >
                <div
                  className={css`
                    padding-top: 8px;
                    margin-right: 10px;
                  `}
                >
                  <Controller
                    name="autoVerifyByRouteOrSession"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        data-e2e="auto-verification-session-option"
                        label={intl.formatMessage({
                          id: 'detail_vulnManagement_policy_autoVerifyBasedOnRouteOrSessionMessage'
                        })}
                        isChecked={enableAutoVerificationTypeChoices}
                        value={String(value)}
                        onChange={(event, value) => {
                          onChange(event)
                          setEnableAutoVerificationTypeChoices(
                            value ? value : false
                          )
                          if (value) {
                            setAutoVerificationType(
                              AutoVerificationType.SESSION
                            )
                          } else {
                            setAutoVerificationType(AutoVerificationType.NONE)
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div
                  className={css`
                    padding-top: 8px;
                    margin-right: 10px;
                    margin-top: 30px;
                    margin-left: -245px;
                  `}
                >
                  <div
                    className={css`
                      display: flex;
                    `}
                  >
                    <Radio
                      label={intl.formatMessage({
                        id: 'detail_vulnManagement_policy_autoVerifyBasedOnSessionMessage'
                      })}
                      isDisabled={!enableAutoVerificationTypeChoices}
                      isChecked={
                        autoVerificationType === AutoVerificationType.SESSION
                      }
                      value={AutoVerificationType.SESSION}
                      onChange={(value) => {
                        if (value) {
                          setAutoVerificationType(AutoVerificationType.SESSION)
                        }
                      }}
                    />
                    <Tooltip
                      body={
                        <div>
                          <div>
                            <FormattedMessage id="detail_vulnManagement_policy_sessionTooltipMessage" />
                          </div>
                        </div>
                      }
                      placement="right"
                    >
                      <div
                        className={css`
                          margin-left: 5px;
                          padding-top: 4px;
                        `}
                      >
                        <IconHelp
                          size="sm"
                          color={
                            enableAutoVerificationTypeChoices
                              ? 'neutral80'
                              : 'neutral40'
                          }
                        />
                      </div>
                    </Tooltip>
                    <Badge
                      color={
                        enableAutoVerificationTypeChoices
                          ? 'neutral90'
                          : 'neutral40'
                      }
                      className={css`
                        margin-left: 5px;
                        font-size: 12px;
                        border-radius: 33px;
                        padding: 0px 6px;
                        max-height: 18px;
                        margin-top: 3px;
                      `}
                    >
                      <FormattedMessage id="badge_recommended" />
                    </Badge>
                  </div>
                  <div
                    className={css`
                      display: flex;
                    `}
                  >
                    <Radio
                      data-e2e="auto-verification-route-option"
                      label={intl.formatMessage({
                        id: 'detail_vulnManagement_policy_autoVerifyBasedOnRouteMessage'
                      })}
                      isDisabled={!enableAutoVerificationTypeChoices}
                      isChecked={
                        autoVerificationType === AutoVerificationType.ROUTE
                      }
                      value={AutoVerificationType.ROUTE}
                      onChange={(value) => {
                        if (value) {
                          setAutoVerificationType(AutoVerificationType.ROUTE)
                        }
                      }}
                    />
                    <Tooltip
                      body={
                        <div>
                          <div>
                            <FormattedMessage id="detail_vulnManagement_policy_routeTooltipPart1Message" />
                          </div>
                          <div
                            className={css`
                              margin-top: 5px;
                            `}
                          >
                            <FormattedMessage id="detail_vulnManagement_policy_routeTooltipPart2Message" />
                          </div>
                        </div>
                      }
                      placement="right"
                    >
                      <div
                        className={css`
                          margin-left: 5px;
                          padding-top: 4px;
                        `}
                      >
                        <IconHelp
                          size="sm"
                          color={
                            enableAutoVerificationTypeChoices
                              ? 'neutral80'
                              : 'neutral40'
                          }
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </Box>
          </Row>
        )}
      </form>
    </div>
  )
}
